import { t } from '@lingui/core/macro'
import { useSnackbar } from '@tecsinapse/cortex-react'
import { useCallback, useState } from 'react'
import { ConfirmModalProps } from '@/core/types/confirm-modal'
import { ClientError } from 'graphql-request'

const useConfirmHelpers = <T>(
  setOpen: (state: boolean) => void,
  onConfirm: ConfirmModalProps<T>['onConfirm'],
  onCancel?: ConfirmModalProps<T>['onCancel'],
  onError?: ConfirmModalProps<T>['onError'],
  successMessage?: ConfirmModalProps<T>['successMessage'],
  errorMessage?: ConfirmModalProps<T>['errorMessage']
) => {
  const [loading, setLoading] = useState(false)
  const { snackbar } = useSnackbar()

  const onClose = useCallback(() => setOpen(false), [setOpen])

  const handleCancel = useCallback(async () => {
    await onCancel?.()
    onClose()
  }, [onCancel, onClose])

  const handleConfirm = useCallback(async () => {
    setLoading(true)
    try {
      await onConfirm()
      if (successMessage !== null) {
        snackbar.show(
          'success',
          successMessage ?? t`Ação realizada com sucesso!`
        )
      }
    } catch (e) {
      const clientError = e as ClientError
      const graphQLErrors = clientError.response?.errors

      if (errorMessage) {
        snackbar.show('error', errorMessage)
      } else if (graphQLErrors?.length) {
        graphQLErrors.forEach(err => snackbar.show('error', err.message))
      } else {
        snackbar.show(
          'error',
          clientError.message ?? t`Não foi possível realizar a ação :(`
        )
      }

      onError?.()
      console.error('[ConfirmModal] Error:', graphQLErrors ?? clientError)
    } finally {
      setLoading(false)
      onClose()
    }
  }, [onConfirm, snackbar, successMessage, errorMessage, onClose, onError])

  return {
    loading,
    onClose,
    handleCancel,
    handleConfirm
  }
}

export default useConfirmHelpers
